
import React, { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { LogOut, User as UserIcon, Settings, HelpCircle, Info, AlertTriangle } from 'lucide-react';
import { useUser } from '@/hooks/useUser';
import { useAuth } from '@/contexts/AuthContext';
import { Skeleton } from "@/components/ui/skeleton";
import { Link, useNavigate } from 'react-router-dom';
import { getVersionSummary } from '@/utils/unifiedVersioning';
import { toast } from 'sonner';

export const UserNav: React.FC = () => {
  const { user: profileUser, isLoading: isUserLoading } = useUser();
  const { user: authUser, signOut, forceSignOut, isLoading: isAuthLoading } = useAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const navigate = useNavigate();

  const handleLogout = async () => {
    console.log('[UserNav] Logout initiated');
    setIsLoggingOut(true);
    try {
      await signOut();
      toast.success('Logged out successfully');
      // Navigate to home as backup (AuthContext should handle this)
      navigate('/', { replace: true });
    } catch (error) {
      console.error('[UserNav] Logout error:', error);
      toast.error('Error logging out');
      // Navigate anyway to prevent user from staying on protected pages
      navigate('/', { replace: true });
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleForceLogout = async () => {
    console.log('[UserNav] Force logout initiated');
    setIsLoggingOut(true);
    try {
      await forceSignOut();
      // Navigate to home as backup (AuthContext should handle this)
      navigate('/', { replace: true });
    } catch (error) {
      console.error('[UserNav] Force logout error:', error);
      // Navigate anyway to clear any cached state
      navigate('/', { replace: true });
    } finally {
      setIsLoggingOut(false);
    }
  };

  const getInitials = (firstName?: string | null, lastName?: string | null) => {
    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    }
    if (firstName) {
      return firstName.charAt(0).toUpperCase();
    }
    if (lastName) {
      return lastName.charAt(0).toUpperCase();
    }
    return 'U'; // Default fallback
  };

  // Show skeleton while auth is loading
  if (isAuthLoading) {
    return <Skeleton className="h-9 w-9 rounded-full" />;
  }

  // Always show UserNav if we have an authenticated user
  if (!authUser) {
    return null;
  }

  // Use profile data if available, otherwise fallback to auth user metadata
  const firstName = profileUser?.first_name || 
                   authUser?.user_metadata?.first_name || 
                   authUser?.user_metadata?.first_name;
                   
  const lastName = profileUser?.last_name || 
                  authUser?.user_metadata?.last_name || 
                  authUser?.user_metadata?.last_name;

  const displayName = firstName && lastName 
    ? `${firstName} ${lastName}`.trim()
    : firstName || lastName || "User";
  
  const userEmail = authUser.email || "No email";
  const initials = getInitials(firstName, lastName);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative h-9 w-9 rounded-full" disabled={isAuthLoading || isLoggingOut}>
          <Avatar className="h-9 w-9">
            <AvatarFallback className="bg-primary/10 text-primary font-medium">
              {initials}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{displayName}</p>
            <p className="text-xs leading-none text-muted-foreground">{userEmail}</p>
            {isUserLoading && (
              <p className="text-xs text-muted-foreground">Loading profile...</p>
            )}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/profile">
            <UserIcon className="mr-2 h-4 w-4" />
            <span>Profile</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/settings">
            <Settings className="mr-2 h-4 w-4" />
            <span>Settings</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/settings?tab=about">
            <Info className="mr-2 h-4 w-4" />
            <span>About</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem disabled>
          <HelpCircle className="mr-2 h-4 w-4" />
          <span>Support</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout} disabled={isAuthLoading || isLoggingOut}>
          <LogOut className="mr-2 h-4 w-4" />
          <span>{isLoggingOut ? 'Logging out...' : 'Log out'}</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleForceLogout} disabled={isAuthLoading || isLoggingOut} className="text-orange-600">
          <AlertTriangle className="mr-2 h-4 w-4" />
          <span>Force Logout</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <div className="px-2 py-1 text-xs text-muted-foreground font-mono">
          {getVersionSummary()}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
